import { ICache } from '../models/cache';
import { ICacheClearArgs } from '../models/cache-clear-args';
import { ICacheReadArgs } from '../models/cache-read-args';
import { ICacheWriteArgs } from '../models/cache-write-args';
import { ICachedValue } from '../models/cached-value';
import {
  cacheRefreshVarianceMS,
  defaultCacheRefreshMS,
} from '../models/constants';
import { ISillyLogger } from '../models/silly-logger';

/*
 This "cacher" maintains objects in memory for a period of time. Serverless wipes out modules after handling a
 request, so storing a local dictionary wouldn't help us -- that's why we store on `global`.
 */
const pendingReadKey = 'pending:read';
/* istanbul ignore else */
if (!global[pendingReadKey]) {
  global[pendingReadKey] = {};
}
const pendingRead: { [hash: string]: Promise<any> } = global[pendingReadKey];

export abstract class Cache implements ICache {
  protected abstract cacheName: string;

  constructor(private logger?: ISillyLogger) {}

  public abstract read<T>(args: ICacheReadArgs<T>): Promise<T>;

  public abstract clear(args?: ICacheClearArgs): Promise<void>;

  public abstract clearByBucket(bucket: string): Promise<void>;

  protected async handleCachedRead<T>(
    cached: ICachedValue,
    args: ICacheReadArgs<T>,
  ): Promise<T> {
    const readingHash = this.cacheName + ':' + getHash(args.bucket, args.id);
    const alreadyReading = Boolean(pendingRead[readingHash]);
    const profileCache = process.env.PROFILE_CACHE?.toLowerCase() === 'true';
    if (cached) {
      if (
        cached.refresh !== 0 &&
        cached.refresh <= Date.now() &&
        !alreadyReading
      ) {
        const promise = args.retrieve();
        pendingRead[readingHash] = promise;
        promise
          .then(value => this.write({ ...args, value }))
          .finally(() => (pendingRead[readingHash] = null));
        if (profileCache && this.logger) {
          this.logger.silly(`cache 🍃 background refresh️ ${readingHash}`);
        }
      } else if (profileCache && this.logger) {
        this.logger.silly(`cache ✅️ ${readingHash}`);
      }
      return cached.value;
    } else {
      try {
        if (profileCache && this.logger) {
          this.logger.silly(
            `cache ❌  ${readingHash}${
              alreadyReading ? ' [joined pending promise]' : ''
            }`,
          );
        }
        const value = await (pendingRead[readingHash] ||
          (pendingRead[readingHash] = args.retrieve()));
        if (!alreadyReading) {
          // Write to the cache in the background without waiting for the result. Gotta go fast!
          this.write({ ...args, value }).then();
        }
        return value;
      } finally {
        pendingRead[readingHash] = null;
      }
    }
  }

  public async write<T>(args: ICacheWriteArgs<T>) {
    this.saveToCache({
      bucket: args.bucket,
      hash: getHash(args.bucket, args.id),
      refresh:
        args.refreshAfterMilliseconds !== 0
          ? Date.now() +
            (args.refreshAfterMilliseconds || defaultCacheRefreshMS) +
            Math.round(Math.random() * cacheRefreshVarianceMS)
          : 0,
      value: args.value,
    });
  }

  protected abstract saveToCache(cached: ICachedValue);
}

export function getHash(bucket: string, id: string | number): string {
  return `${bucket}:${id}`;
}
